import { useMemo, useState } from 'react';
import { Button, Skeleton } from '@indipia/ui-core';
import { ThumbImg } from '../components/ThumbImg';
import { PhotoLightbox } from '../components/PhotoLightbox';
import { useHolidays } from '../hooks/useHolidays';
import { presetRange, type DatePreset as Preset } from '../config/datePreset';
import { Toast } from '../components/Toast';
import { SitePanelProvider } from '../components/site-panel/SitePanelContext';
import { SiteArea } from '../components/site-panel/SiteArea';
import { useSiteSelection } from '../components/site-panel/useSiteSelection';
import { WorklogFeedGroups } from '../components/worklog-feed/WorklogFeedGroups';
import { WorklogFeedToolbar } from '../components/worklog-feed/WorklogFeedToolbar';
import { buildPairGroups, filterSortFeed, toDash, toFeedItems } from '../components/worklog-feed/feedModel';
import { useFeedPartners } from '../components/worklog-feed/useFeedPartners';
import { useIndiPhotoWork } from '../components/worklog-feed/useIndiPhotoWork';
import { useWorklogFeed } from '../components/worklog-feed/useWorklogFeed';
import type { BasicInfoPartner } from '../types/worklog.types';

/**
 * 공사현황 통합조회 (site-reports) — 사장님 콘솔 (§1.5·§2.5 진입점4, §5.4.6).
 *
 * 서버 통합 타임라인(§1.5)이 작업요청(§2.1)+현장보고(§3.1)를 병합·페이징해 내려주고, 이 화면은 그 피드를 날짜 그룹으로 그린다.
 * 좌/우 레이아웃·조회조건 바·필터칩·정렬/보기는 WorkOrderListPage·FieldReportListPage 패턴을 따른다.
 *
 * 현장보고는 작업요청일 기준으로 나열한다(서버가 연결된 작업요청의 발송일시를 occurredAt으로 내려줌).
 * 작업요청↔현장보고는 날짜 그룹 안에서 FK(workOrderId, 결정 4-1)로 매칭해 좌(작업요청)·우(현장보고)
 * 페어 행으로 배치한다(REQ-4-01). 고객공유 선정 요소는 이 화면에 두지 않는다(REQ-4-06, 결정 4-8 —
 * 진입점5 `CustomerReportEditPage`로 이관). 인디포토 저장(§3.8)만 카드 안 버튼으로 남는다(REQ-4-04).
 *
 * 조회·카드·페어 행·조회조건 바·인디포토 담기는 `components/worklog-feed/`로 분리해, 나의 현장 작업공간의
 * '공사현황' 탭(SiteWorkFeed, REQ-11-07)과 같이 쓴다. 이 화면은 현장 목록 패널로 현장을 고르는 전체 화면 쪽이다.
 */

export function WorklogStatusPage() {
  return (
    <SitePanelProvider>
      <WorklogStatusInner />
    </SitePanelProvider>
  );
}

function WorklogStatusInner() {
  // ── 조회조건(기간) ──
  const [preset, setPreset] = useState<Preset>('tw'); // 기본 '지난주'(결정 4-8) — 공통 프리셋엔 해당 옵션이 없어 '이번주'로 대체
  const [customRange, setCustomRange] = useState<{ from: string; to: string } | null>(null);
  const holidays = useHolidays();
  const range = customRange
    ? { from: new Date(`${customRange.from}T00:00:00`), to: new Date(`${customRange.to}T00:00:00`) }
    : presetRange(preset);

  // ── 결과 필터/보기 ──
  const [selectedPartners, setSelectedPartners] = useState<BasicInfoPartner[]>([]);
  const [sortDesc, setSortDesc] = useState(true);

  // ── 작성자 목록(worklogStore 반장 재사용 — 직접 조회 안 함) ──
  const { partners, partnersLoaded, partnersError } = useFeedPartners();

  // ── 로딩 게이트: ①반장(store) + ②현장(SiteArea) 완료 후 ③통합 피드 조회 ──
  const [sitesLoaded, setSitesLoaded] = useState(false);
  const baseLoaded = partnersLoaded && sitesLoaded;

  // 현장목록 공통 선택 규칙(REQ-C-01, 토글 단일선택) — 3개 목록 화면 공용 훅
  const { selectedSiteId, isSiteSelected, toggleSite, selectOnlySite, onSitesLoaded: handleSitesLoaded } = useSiteSelection();

  // ── 통합 피드 조회: 서버로는 기간만 보낸다. 반장·유형·현장·공유필터는 로드된 피드에 클라이언트에서 적용한다. ──
  const { feedItems, state, hasMore, loadingMore, reload, scrollRef, sentinelRef } = useWorklogFeed({
    dateFrom: toDash(range.from),
    dateTo: toDash(range.to),
    enabled: baseLoaded,
  });

  // ── 인디포토 담기(선정) — 현장보고 1건 단위, 인디포토 저장만 남음(REQ-4-06) ──
  const work = useIndiPhotoWork(feedItems);

  // ── 라이트박스 ──
  const [lb, setLb] = useState<{ frId: number; photoIdx: number } | null>(null);

  // ── 병합: WO+FR → 공통 피드로 정규화 후 현장·작성자 필터 + dtm 기준 정렬 ──
  const selectedIds = useMemo(() => new Set(selectedPartners.map((p) => p.partnerId)), [selectedPartners]);
  const mergedItems = useMemo(
    () => filterSortFeed(toFeedItems(feedItems), { siteFilter: isSiteSelected, partnerIds: selectedIds, sortDesc }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [feedItems, selectedSiteId, selectedIds, sortDesc],
  );

  // 작업요청↔현장보고 페어 그룹(REQ-4-01) — 날짜 그룹 안에서 workOrderId로 매칭.
  const groups = useMemo(() => buildPairGroups(mergedItems), [mergedItems]);

  const lbPhotos = useMemo(() => {
    if (!lb) return [];
    const fr = feedItems.find((f) => f.type === 'FIELD_REPORT' && f.fieldReport?.fieldReportId === lb.frId)?.fieldReport;
    return (fr?.photos ?? []).map((p) => ({ url: p.url || p.thumbUrl }));
  }, [lb, feedItems]);

  return (
    <div className="flex h-full">
      <div className="flex min-w-0 flex-1 flex-col bg-surface">
        {/* ── 조회조건 바(기간 · 반장님 · 정렬) ── */}
        <WorklogFeedToolbar
          range={range}
          preset={preset}
          customRange={customRange}
          onPresetChange={setPreset}
          onCustomRangeChange={setCustomRange}
          holidays={holidays}
          partners={partners}
          partnersError={partnersError}
          selectedPartners={selectedPartners}
          onPartnersChange={setSelectedPartners}
          sortDesc={sortDesc}
          onToggleSort={() => setSortDesc((v) => !v)}
        />

        {/* 선정(작업)은 카드 단건 인라인으로 처리한다 — 전역 모드 바 없음(FieldReportCard 하단 바 참조) */}

        {/* ── 통합 피드 ── */}
        <div ref={scrollRef} className="flex-1 overflow-auto px-6 pb-10">
          {state === 'loading' && (
            <div className="space-y-3 pt-3">
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} height={112} className="rounded-lg" />
              ))}
            </div>
          )}

          {state === 'error' && (
            <div className="mt-6 flex flex-col items-center gap-3 rounded-lg border border-border bg-surface-raised py-16">
              <span className="text-sm text-text-secondary">공사현황을 불러오지 못했습니다.</span>
              <Button variant="secondary" size="sm" onClick={reload}>
                다시 조회
              </Button>
            </div>
          )}

          {state === 'ready' && mergedItems.length === 0 && (
            <div className="mt-6 flex flex-col items-center gap-2 rounded-lg border border-border bg-surface-raised py-20 text-center">
              {selectedSiteId === null ? (
                // 현장목록 전체 해제 상태 — 이 화면(진입점4) 전용 안내(설계서 §2.1.1)
                <div className="text-heading-sm font-bold text-text">현장을 선택하세요</div>
              ) : (
                <>
                  <div className="text-heading-sm font-bold text-text">표시할 내용이 없습니다</div>
                  <div className="text-body-md leading-relaxed text-text-secondary">
                    기간·유형·작성자·현장 조건을 바꿔보세요.
                  </div>
                </>
              )}
            </div>
          )}

          {state === 'ready' && groups.length > 0 && (
            <WorklogFeedGroups
              groups={groups}
              work={work}
              onOpenPhoto={(frId, photoIdx) => setLb({ frId, photoIdx })}
            />
          )}

          {/* 무한스크롤 센티넬 + 추가 로딩 표시 */}
          {state === 'ready' && hasMore && (
            <div ref={sentinelRef} className="flex items-center justify-center py-5 text-body-sm text-text-muted">
              {loadingMore ? '불러오는 중…' : ''}
            </div>
          )}
        </div>
      </div>

      {/* 우측 공사현장 목록 패널 */}
      <SiteArea
        onFirstLoad={() => setSitesLoaded(true)}
        isSiteSelected={isSiteSelected}
        onToggleSite={toggleSite}
        onSitesLoaded={handleSitesLoaded}
        onSiteRegistered={selectOnlySite}
        onSiteDeleted={() => {
          // 삭제된 현장의 작업요청·현장보고는 이미 조회해 둔 피드에 남아있으므로 재조회로 반영한다.
          reload();
        }}
      />

      <PhotoLightbox
        photos={lbPhotos}
        index={lb?.photoIdx ?? null}
        onClose={() => setLb(null)}
        onIndexChange={(i) => setLb((prev) => (prev ? { ...prev, photoIdx: i } : prev))}
        renderImage={(photo, style) => <ThumbImg url={photo.url} alt="" imgStyle={style} />}
      />

      <Toast message={work.toastMsg} onClose={work.clearToast} variant="info" />
    </div>
  );
}
